import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Style/Common.css";
import "./Style/Header.css";
import Loading from "../Common/Loading";
import LoggedOut from "../LOGIN/LoggedOut";
import { decryptPayload } from "../Common/utils/Decrypt";

interface User {
  Login: string;
  FName: string;
  LName?: string;
  ActivityStatus: string;
  Std: string;
  CurrentAvatar: string | null;
  Name: string;
  PhoneNo: string;
  Website: string;
  AccessToken?: string;
  RefreshToken?: string;
}

type AvatarItem = { name: string; url: string };

const DEFAULT_AVATAR_NAMES = ["lion", "capybara", "otter", "taco"];
const FALLBACK_DEFAULT_AVATAR_BASE_URL = "/assets/AvatarNew/DefaultAvatar";

const MSG_VACATION =
  "Student access temporarily disabled. Please contact provider.";
const MSG_INACTIVE = "User is In-Active.";

const normalizeActivityStatus = (status?: string) => {
  const normalized = (status ?? "").trim().toUpperCase();

  if (!normalized || normalized === "A" || normalized === "ACTIVE") {
    return "A";
  }

  if (normalized === "V" || normalized === "VACATION") {
    return "V";
  }

  return normalized;
};

const getDefaultAvatarName = (login: string, firstName: string) => {
  if (firstName.trim() === "Guest") return "guest";

  const childNumber = /^child(\d+)$/i.exec(login)?.[1];
  const parsedChildNumber = Number(childNumber);
  if (!childNumber || !Number.isInteger(parsedChildNumber) || parsedChildNumber < 1) {
    return "taco";
  }

  return DEFAULT_AVATAR_NAMES[(parsedChildNumber - 1) % DEFAULT_AVATAR_NAMES.length];
};

const SelectUser: React.FC = () => {
  const [UserData, setUserData] = useState<User[]>([]);
  const [avatars, setAvatars] = useState<AvatarItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [loadingUserClick, setLoadingUserClick] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [defaultAvatarBaseUrl, setDefaultAvatarBaseUrl] = useState(
    FALLBACK_DEFAULT_AVATAR_BASE_URL
  );
  const navigate = useNavigate();
  const [isPageLoading, setIsPageLoading] = useState(false);

  // ---- UI-only state (inline-style responsiveness / hover) ----
  const [winW, setWinW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1366
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [logoutHover, setLogoutHover] = useState(false);
  const [continueHover, setContinueHover] = useState(false);

  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  const rawLogo = sessionStorage.getItem("WebsiteLogo");
  let logoUrl: string | null = rawLogo || null;

  // track window width so inline styles can be responsive
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.body.style.background = "#fff";
    document.body.style.height = "100%";
  }, []);

  // load Open Sans (same font as the legacy page)
  useEffect(() => {
    const id = "su-open-sans-font";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // POST + decrypt helper (throws if decryption fails)
  const postEncrypted = async <T,>(path: string, body: unknown): Promise<T> => {
    const response = await fetch(`${apiUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const raw = await response.json();
    const data = decryptPayload<T>(raw.data);
    if (!data) throw new Error(`Decryption failed (${path})`);
    return data;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      const schoolCode = sessionStorage.getItem("SchoolCode");
      const familyCode = sessionStorage.getItem("FamilyCode");

      try {
        // Logo does not depend on users, so it runs in parallel.
        const logoPromise = postEncrypted<{ imageUrl: string }>(
          "getWebsiteLogo/",
          { SchoolCode: schoolCode }
        ).then((logoData) => {
          sessionStorage.setItem("WebsiteLogo", logoData.imageUrl);
        });

        // Users, then avatars (avatars need the user list).
        const usersPromise = (async () => {
          const users = await postEncrypted<User[]>("SelectUser/", {
            FamilyCode: familyCode,
            SchoolCode: schoolCode,
          });
          setUserData(users);

          const avatarNames = users
            .map((u: User) => u.CurrentAvatar)
            .filter(Boolean);

          const avatarData = await postEncrypted<{
            avatars: AvatarItem[];
            defaultAvatarBaseUrl?: string;
          }>("getAvatarsImages/", { avatarNames });

          setAvatars(avatarData.avatars);
          const resolvedBase =
            avatarData.defaultAvatarBaseUrl || FALLBACK_DEFAULT_AVATAR_BASE_URL;
          setDefaultAvatarBaseUrl(resolvedBase);

          return { users, resolvedBase, avatarList: avatarData.avatars };
        })();

        const [{ users, resolvedBase, avatarList }] = await Promise.all([
          usersPromise,
          logoPromise,
        ]);

        if (users.length === 1) {
          const user = users[0];
          handleUserClick(
            user.Login,
            user.FName,
            user.CurrentAvatar,
            user.Std,
            resolvedBase,
            user.ActivityStatus,
            avatarList
          );
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Unable to load user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [apiUrl]);

  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!url) return resolve(false);
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  const getAvatarUrl = (
    currentAvatar: string | null,
    login: string,
    firstName: string,
    avatarBaseUrl = defaultAvatarBaseUrl,
    avatarList: AvatarItem[] = avatars
  ) => {
    if (firstName.trim() === "Guest") {
      return `${avatarBaseUrl}/guest_avatar.png`;
    }

    const avatarName = currentAvatar || getDefaultAvatarName(login, firstName);
    if (!currentAvatar) {
      return `${avatarBaseUrl}/${avatarName}_avatar.png`;
    }

    return (
      avatarList.find((avatar) => avatar.name === currentAvatar)?.url ||
      currentAvatar
    );
  };

  const handleUserClick = async (
    login: string,
    fName: string,
    CurrentAvatar: string | null,
    Std: string,
    avatarBaseUrl = defaultAvatarBaseUrl,
    activityStatus: string = "A",
    avatarList: AvatarItem[] = avatars
  ) => {
    // Same rules as the VB btnContinue_Click
    const status = normalizeActivityStatus(activityStatus);
    if (status === "V") {
      window.alert(MSG_VACATION);
      return;
    }
    if (status !== "A") {
      window.alert(MSG_INACTIVE);
      return;
    }

    setIsPageLoading(true);

    sessionStorage.setItem("Login", login);
    sessionStorage.setItem("FName", fName.trim());
    sessionStorage.setItem("Grade", Std);
    sessionStorage.removeItem("Subject");

    const familyCode = sessionStorage.getItem("FamilyCode");
    const schoolCode = sessionStorage.getItem("SchoolCode");

    // Token, cache clear and avatar check are independent: run together.
    const tokenPromise = (async () => {
      const tokenResponse = await fetch(`${apiUrl}GenerateToken/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Login: login,
          FamilyCode: familyCode,
          SchoolCode: schoolCode,
        }),
      });
      const encryptedToken = await tokenResponse.json();
      const tokenData = decryptPayload(encryptedToken.data);
      if (!tokenData) throw new Error("Token generation failed");
      return tokenData;
    })();

    const cachePromise = fetch(`${apiUrl}clear_cache/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        SchoolCode: schoolCode,
        FamilyCode: familyCode,
        Login: login,
        Caches: ["all_user"],
      }),
    }).catch((cacheError) => {
      console.error("Do More cache clearing error:", cacheError);
    });

    let avatarUrl = getAvatarUrl(
      CurrentAvatar,
      login,
      fName,
      avatarBaseUrl,
      avatarList
    );
    const imagePromise = checkImageExists(avatarUrl);

    try {
      const [tokenData, , exists] = await Promise.all([
        tokenPromise,
        cachePromise,
        imagePromise,
      ]);

      localStorage.setItem("access_token", tokenData.AccessToken);
      localStorage.setItem("refresh_token", tokenData.RefreshToken);

      if (!exists) {
        avatarUrl = `${avatarBaseUrl}/${getDefaultAvatarName(
          login,
          fName
        )}_avatar.png`;
      }
    } catch (error) {
      console.error("Token generation error:", error);
      setError("Unable to login. Please try again.");
      setIsPageLoading(false);
      return;
    }

    sessionStorage.setItem("CurrentAvatar", avatarUrl);

    navigate("/STUDENT/MSR/StudyRoom?From=SelectUser", { replace: true });
  };

  // ==========================================================
  // Inline styles (mirror legacy select_user.css breakpoints)
  // >1700px = large | <=1700px = normal | <=1000px | <=420px
  // ==========================================================
  const isLarge = winW > 1700;
  const isMid = winW <= 1000;
  const isSmall = winW <= 420;

  const logoW = isLarge ? 200 : 150;
  const titleSize = isLarge ? 40 : 24;
  const avatarSize = isLarge ? 170 : 120;
  const gapY = isLarge ? 30 : 20;
  const gapX = isLarge ? 40 : isSmall ? 24 : 50;
  const containerMt = isLarge ? 50 : 20;
  const mainMb = isLarge ? 50 : 0;
  const continueMt = isLarge ? 20 : 0;
  const mainWidth = isLarge
    ? "min(60%, 1400px)"
    : isSmall
    ? "94%"
    : isMid
    ? "min(90%, 1000px)"
    : "min(70%, 1000px)";

  const showContinue = !loading && !error && !!selectedUser;
  const FONT = "'Open Sans', sans-serif";

  const pageStyle: React.CSSProperties = {
    boxSizing: "border-box",
    minHeight: "100vh",
    minWidth: 320,
    margin: 0,
    padding: 0,
    background: "#fff",
    fontFamily: FONT,
  };

  const headerStyle: React.CSSProperties = {
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    height: 80,
    margin: "0 auto",
    padding: 0,
    background: "#fff",
    fontSize: 0,
  };

  const logoStyle: React.CSSProperties = {
    boxSizing: "border-box",
    display: "inline-block",
    flex: "0 1 auto",
    width: logoW,
    height: 60,
    margin: "10px auto 10px 20px",
    padding: 0,
    backgroundImage: logoUrl ? `url(${logoUrl})` : "none",
    backgroundPosition: "left",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
  };

  const logoutStyle: React.CSSProperties = {
    boxSizing: "border-box",
    display: "block",
    flex: "0 0 auto",
    width: 150,
    height: 40,
    margin: "20px 20px 0 0",
    padding: 0,
    borderRadius: 12,
    background: logoutHover ? "#e55c00" : "#ff6600",
    color: "#fff",
    cursor: "pointer",
    fontFamily: FONT,
    fontSize: 15,
    lineHeight: "40px",
    textAlign: "center",
    textDecoration: "none",
    userSelect: "none",
  };

  const mainStyle: React.CSSProperties = {
    boxSizing: "border-box",
    width: mainWidth,
    maxWidth: isLarge ? "none" : 800,
    margin: `0 auto ${mainMb}px`,
    padding: 0,
    textAlign: "center",
  };

  const titleStyle: React.CSSProperties = {
    margin: "0 0 10px",
    padding: 0,
    background: "transparent",
    color: "#000",
    fontFamily: FONT,
    fontSize: titleSize,
    fontWeight: 700,
    lineHeight: "45px",
    textAlign: "center",
  };

  const containerStyle: React.CSSProperties = {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: `${gapY}px ${gapX}px`,
    width: "100%",
    marginTop: containerMt,
    padding: 0,
    background: "transparent",
  };

  const userStyle = (
    index: number,
    isSelected: boolean
  ): React.CSSProperties => ({
    boxSizing: "border-box",
    position: "relative",
    display: "block",
    flex: "0 0 auto",
    width: avatarSize,
    height: avatarSize,
    margin: "0 0 60px",
    padding: 0,
    border: 0,
    outline: "none",
    boxShadow: "none",
    background: "transparent",
    color: "#000",
    cursor: "pointer",
    fontFamily: FONT,
    overflow: "visible",
    transform:
      hoveredIndex === index || isSelected
        ? "translateY(-5px)"
        : "translateY(0)",
    transition: "transform 0.2s ease",
  });

  const avatarWrapStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    height: "100%",
    margin: 0,
    padding: 0,
    overflow: "hidden",
    borderRadius: "50%",
    background: "#ffe4d1",
  };

  const avatarImgStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    height: "100%",
    margin: 0,
    padding: 0,
    border: 0,
    borderRadius: "50%",
    background: "#ffe4d1",
    objectFit: "cover",
  };

  const nameStyle = (isSelected: boolean): React.CSSProperties => ({
    boxSizing: "border-box",
    position: "absolute",
    top: "calc(100% + 10px)",
    left: "50%",
    zIndex: 1,
    minWidth: 150,
    margin: 0,
    padding: 5,
    border: `0.5px solid ${isSelected ? "#ff6600" : "transparent"}`,
    borderRadius: 6,
    background: isSelected ? "#fff3eb" : "#f7f7f7",
    color: "#000",
    fontFamily: FONT,
    fontSize: 16,
    lineHeight: "normal",
    textAlign: "center",
    whiteSpace: "nowrap",
    transform: "translateX(-50%)",
    pointerEvents: "none",
  });

  const continueWrapStyle: React.CSSProperties = {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginTop: continueMt,
    padding: 0,
    opacity: showContinue ? 1 : 0,
    visibility: showContinue ? "visible" : "hidden",
    pointerEvents: showContinue ? "auto" : "none",
    transform: showContinue ? "translateY(0)" : "translateY(10px)",
    transition: "all 0.25s ease",
  };

  const continueStyle: React.CSSProperties = {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 240,
    height: 64,
    margin: "0 0 50px",
    padding: 0,
    border: 0,
    borderRadius: 18,
    background: continueHover ? "#e55c00" : "#ff6600",
    color: "#fff",
    cursor: "pointer",
    fontFamily: FONT,
    fontSize: 15,
    userSelect: "none",
    transform: continueHover ? "translateY(-2px)" : "translateY(0)",
    transition: "background-color 0.2s ease, transform 0.2s ease",
  };

  return (
    <form id="" className="select-user-page" style={pageStyle}>
      {isPageLoading && (
        <div className="loading-overlay">
          <Loading />
        </div>
      )}

      <div>
        {/* Top bar */}
        <div id="select_user_header" style={headerStyle}>
          <div id="select_user_logo" style={logoStyle}></div>
          <a
            href="/"
            id="select_user_logout"
            style={logoutStyle}
            onMouseEnter={() => setLogoutHover(true)}
            onMouseLeave={() => setLogoutHover(false)}
            onClick={(e) => {
              e.preventDefault();
              localStorage.clear();
              sessionStorage.clear();
              navigate("/Login/LoggedOut");
            }}
          >
            Log out
          </a>
        </div>

        {/* Title + users */}
        <div id="select_user_main" style={mainStyle}>
          <h1 style={titleStyle}>Select User</h1>

          <div id="user_container" style={containerStyle}>
            {loading || loadingUserClick ? (
              <Loading />
            ) : error ? (
              <p className="error_message">{error}</p>
            ) : UserData.length === 0 ? (
              <LoggedOut />
            ) : (
              UserData.map((user, index) => {
                const avatarUrl = getAvatarUrl(
                  user.CurrentAvatar,
                  user.Login,
                  user.FName
                );
                const isSelected = selectedUser?.Login === user.Login;
                return (
                  <button
                    key={index}
                    id={`divUser${index}`}
                    type="button"
                    data-counter={index + 1}
                    style={userStyle(index, isSelected)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onFocus={() => setHoveredIndex(index)}
                    onBlur={() => setHoveredIndex(null)}
                    onClick={() => setSelectedUser(user)}
                  >
                    <span style={avatarWrapStyle}>
                      <img
                        id={`imgUserAvatar${index}`}
                        src={
                          avatarUrl && avatarUrl !== ""
                            ? avatarUrl
                            : `${defaultAvatarBaseUrl}/taco_avatar.png`
                        }
                        alt=""
                        style={avatarImgStyle}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `${defaultAvatarBaseUrl}/${getDefaultAvatarName(
                            user.Login,
                            user.FName
                          )}_avatar.png`;
                        }}
                      />
                    </span>
                    <span style={nameStyle(isSelected)}>
                      {user.FName.trim()}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Continue: always rendered so layout doesn't jump (same as legacy) */}
        <div style={{ marginTop: 20 }}>
          <div id="continue_container" style={continueWrapStyle}>
            <button
              type="button"
              style={continueStyle}
              disabled={!selectedUser || loadingUserClick}
              onMouseEnter={() => setContinueHover(true)}
              onMouseLeave={() => setContinueHover(false)}
              onClick={() =>
                selectedUser &&
                handleUserClick(
                  selectedUser.Login,
                  selectedUser.FName,
                  selectedUser.CurrentAvatar,
                  selectedUser.Std,
                  defaultAvatarBaseUrl,
                  selectedUser.ActivityStatus
                )
              }
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SelectUser;